/**
 * Scroll-driven scrub for text animations.
 *
 * For every `[data-animation-scrub="true"]` text element we drive the
 * animation progress directly from the element's viewport position
 * (0 when the element top is at the viewport bottom, 1 once it reaches
 * `data-animation-scrub-end-at-top` % from the top — or once it has fully
 * exited the top, when `data-animation-scrub-end-at-full-exit` is set).
 * Same math as the image/video scroll-animations (shared `viewport_progress`
 * util), so the two systems feel consistent to the user.
 *
 * Per animation type:
 *   - masked_lines / masked_words / fade_lines / fade_words /
 *     fade_whole_text / fade_and_slide_whole_text / typewriter_fade
 *       → build the existing anime.js timeline once (paused, autoplay false)
 *         and `timeline.seek(progress * timeline.duration)` each tick;
 *         anime.js does the per-target interpolation for free.
 *   - typewriter
 *       → no timeline (the timeline-only fires its char-opacity setTimeouts
 *         from `begin`, which we never invoke). Instead set per-char opacity
 *         directly: char i is opacity 1 once `progress >= i / n`. Reverses
 *         naturally on scroll up.
 *
 * The IO-driven `playAnimation` path in `textanimations.js` skips elements
 * with `data-animation-scrub="true"` (see initObserver) so the two systems
 * never both touch the same target.
 *
 * Lifecycle:
 *   - `texts_are_split` (after splitting + applyPreRevealVisualStateToTarget):
 *     refresh pairings — pick up newly-split elements, drop ones whose DOM
 *     went away.
 *   - `newpageshown`: extra refresh pass for AJAX navigation safety net.
 *   - `prefers-reduced-motion: reduce`: snap to end state, stop loop.
 *   - capture-phase scroll listener: catches scrolls inside
 *     `#lay-project-overlay` and `.lay-overlay > .lay-content` too (scroll
 *     events don't bubble but they do dispatch through capture).
 */

import { getProgress, getNaturalViewportRect, getScrollContainerForElement, getViewportContext } from '../scroll_animations/viewport_progress'
import { createAnimationTimeline, animationTimelines } from './textanimations'
import { setLineTextMaskRevealClip } from './util'
import { getWordLevelAnimationTargets, getCharacterLevelAnimationTargets } from './text_animation_targets'
import { applyPreRevealVisualStateToTarget } from './text_animation_resplit_recovery'
import {
	clearTypewriterBackgroundReveal,
	prepareTypewriterBackgroundReveal,
	setTypewriterBackgroundRevealProgress,
} from './typewriter_background_reveal'
import animation_lifecycle_controller from '../animations/lifecycle/animation_lifecycle_controller'

const SCRUB_ATTR = 'data-animation-scrub'
const START_AT_ATTR = 'data-animation-scrub-start-at'
const END_AT_TOP_ATTR = 'data-animation-scrub-end-at-top'
const END_AT_FULL_EXIT_ATTR = 'data-animation-scrub-end-at-full-exit'
const NEAR_VIEWPORT_MARGIN_VH = 0.5
const SCROLL_IDLE_TIMEOUT_MS = 600

/** target Element → { type, endAtTop, endAtFullExit, timeline?, characters? } */
const pairings = new Map()
let rafId = null
let introRafId = null
let scrollIdleTimeoutId = null
let listenersAttached = false
let prefersReducedMotion = false

function isRectNearViewport(rect, vh) {
	const margin = vh * NEAR_VIEWPORT_MARGIN_VH
	return rect.top < vh + margin && rect.bottom > -margin
}

function shouldHoldPairingAtZeroDuringIntro(target) {
	const inOverlay = !!target.closest('#lay-project-overlay')
	if (inOverlay) {
		return animation_lifecycle_controller.isIntroPendingFor('overlay')
	}
	// Normal-page scrub targets used to be held at zero while a nav-sync
	// session was in flight so they could fade in lockstep with the
	// staggered nav animation. Now that nav-sync is removed, scrub targets
	// always follow the user's scroll position regardless of an intro
	// being pending.
	return false
}

function directionForType(target, animationType) {
	const direction = target.getAttribute('data-direction')
	if (direction === 'left' || direction === 'right' || direction === 'up' || direction === 'down' || direction === 'bottomLeft' || direction === 'bottomRight') {
		return direction
	}
	if (animationType === 'typewriter_words' || animationType === 'typewriter') {
		return 'left'
	}
	return 'up'
}

function shouldReverseTypewriterScrub(target, animationType) {
	const readingDirection = target.getAttribute('data-reading-direction')
	if (readingDirection === 'rtl') {
		return true
	}
	if (readingDirection === 'ltr') {
		return false
	}
	if (animationType === 'typewriter_lines') {
		return directionForType(target, animationType) === 'down'
	}
	return directionForType(target, animationType) === 'right'
}

function applyTypewriterFadeProgress(pairing, progress) {
	const chars = pairing.characters
	if (!chars) return
	const n = chars.length
	if (n === 0) return
	const t = progress * pairing.totalDuration
	const ease = pairing.easingFn
	const baseDelay = pairing.baseDelay
	const initialDelay = pairing.initialDelay
	const tweenDuration = pairing.tweenDuration

	let cache = pairing._lastOpacities
	if (!cache || cache.length !== n) {
		cache = new Array(n).fill(-1)
		pairing._lastOpacities = cache
	}

	// Per-char per-tick: derive opacity from where `t` sits inside the char's
	// own [start, end] tween window, cache the result, and only write to
	// `style.opacity` when the rounded value actually changed. Stable chars
	// (fully shown above `t`, fully hidden below it) become free after one
	// settle frame.
	for (let i = 0; i < n; i++) {
		const charStart = initialDelay + i * baseDelay
		const charEnd = charStart + tweenDuration
		let op
		if (t <= charStart) op = 0
		else if (t >= charEnd) op = 1
		else op = ease((t - charStart) / tweenDuration)
		// Round to 3 decimals — finer than the eye, plus prevents thrash
		// from sub-pixel float jitter producing different strings.
		const rounded = Math.round(op * 1000) / 1000
		if (rounded !== cache[i]) {
			chars[i].style.opacity = rounded === 0 ? '0' : rounded === 1 ? '1' : String(rounded)
			cache[i] = rounded
		}
	}
}

function applyTypewriterProgress(target, pairing, progress) {
	const characters = pairing.characters
	if (!characters) return
	const n = characters.length
	if (n === 0) return
	// Translate progress into "how many chars should be visible right now".
	// STRICT `>` so progress=0 yields 0 visible chars (the start-offset gap
	// must show nothing). visibleCount ranges 0..n.
	let visibleCount = 0
	for (let i = 0; i < n; i++) {
		if (progress > i / n) visibleCount = i + 1
	}
	const last = pairing._lastVisibleCount | 0
	if (last === visibleCount) return // no-op tick
	// Only touch the chars whose visibility actually flipped — typically 1,
	// occasionally a few when scroll velocity skips multiple thresholds in
	// one rAF tick. Avoids the O(n) style-read+write storm we had before.
	const lo = pairing.reverse
		? n - (last < visibleCount ? visibleCount : last)
		: (last < visibleCount ? last : visibleCount)
	const hi = pairing.reverse
		? n - (last < visibleCount ? last : visibleCount)
		: (last < visibleCount ? visibleCount : last)
	const opacityShown = visibleCount > last ? '1' : '0'
	for (let i = lo; i < hi; i++) {
		characters[i].style.opacity = opacityShown
	}
	pairing._lastVisibleCount = visibleCount
	setTypewriterBackgroundRevealProgress(target, visibleCount, pairing.reverse)
}

function applyTimelineProgress(timeline, progress) {
	if (!timeline) return
	const dur = typeof timeline.duration === 'number' ? timeline.duration : 0
	if (dur <= 0) return
	// `seek` jumps the timeline; anime.js applies all per-target tweens.
	// Pause defensively in case something else nudged it into playing.
	if (!timeline.paused) timeline.pause()
	timeline.seek(progress * dur)
}

function applyPairingProgress(target, pairing, progress) {
	if (pairing.type === 'typewriter') {
		applyTypewriterProgress(target, pairing, progress)
	} else if (pairing.type === 'typewriter_fade') {
		applyTypewriterFadeProgress(pairing, progress)
	} else {
		applyTimelineProgress(pairing.timeline, progress)
	}
}

function updatePairing(target, pairing, rect, ctx) {
	const progress = getProgress(rect, pairing.endAtTop, pairing.endAtFullExit, pairing.startAt, ctx)
	applyPairingProgress(target, pairing, progress)
}

function snapPairingToEnd(target, pairing) {
	applyPairingProgress(target, pairing, 1)
}

function getCurrentProgressForPairing(target, pairing) {
	const rect = getNaturalViewportRect(target)
	const ctx = getViewportContext(getScrollContainerForElement(target))
	return getProgress(rect, pairing.endAtTop, pairing.endAtFullExit, pairing.startAt, ctx)
}

function cancelIntroAnimation() {
	if (introRafId !== null) {
		cancelAnimationFrame(introRafId)
		introRafId = null
	}
}

function getIntroDurationMs() {
	const configured = window.layData ? parseInt(window.layData.navigation_transition_in_duration, 10) : 0
	if (isFinite(configured) && configured > 0) {
		return Math.min(Math.max(configured, 220), 700)
	}
	return 360
}

function easeIntroProgress(progress) {
	if (typeof anime !== 'undefined' && typeof anime.easing === 'function') {
		return anime.easing('easeOutCubic')(progress)
	}
	return 1 - Math.pow(1 - progress, 3)
}

function animateInitialPairingsToCurrentProgress(targets) {
	cancelIntroAnimation()
	const targetsArray = Array.isArray(targets) ? targets : Array.from(targets)
	const items = []
	targetsArray.forEach((target) => {
		const pairing = pairings.get(target)
		if (!target || !pairing) return
		const to = getCurrentProgressForPairing(target, pairing)
		applyPairingProgress(target, pairing, 0)
		if (to > 0) {
			items.push({ target, pairing, to, delay: 0 })
		}
	})
	if (items.length === 0) {
		onScrollOrResize()
		return
	}

	const duration = getIntroDurationMs()
	const start = performance.now()
	const step = (now) => {
		const elapsed = now - start
		let allDone = true
		items.forEach(({ target, pairing, to, delay }) => {
			if (!target || !target.isConnected || !pairings.has(target)) return
			const localT = Math.min(1, Math.max(0, (elapsed - delay) / duration))
			const eased = easeIntroProgress(localT)
			applyPairingProgress(target, pairing, to * eased)
			if (localT < 1) {
				allDone = false
			}
		})
		if (!allDone) {
			introRafId = requestAnimationFrame(step)
			return
		}
		introRafId = null
		onScrollOrResize()
	}
	introRafId = requestAnimationFrame(step)
}

const tick = () => {
                     
	const _perfTickStart = performance.now()
           

	// Under fullpage.js (JS variant of fullscreen-slider), `textanimations.js`
	// plays scrub text-animations as one-shot timelines on `fpAfterLoad` /
	// `fpAfterSlideLoad`. fp doesn't scroll the window, so seeking the
	// timeline based on scroll position here would just jump it back to
	// progress=0 every tick and visually freeze the animation. Bail out so
	// the one-shot `play()` from textanimations.js can run uninterrupted.
	// CSS scroll-snap path is unaffected — it uses native scroll, so scrub
	// works the way it's supposed to.
	if (typeof document !== 'undefined' && document.documentElement && document.documentElement.classList.contains('fp-enabled')) {
		rafId = null
		return
	}

	// Single viewport snapshot per tick — `getProgress` would otherwise re-
	// read `documentElement.scrollHeight` (layout-trigger) once per pairing.
	let anyNear = false
	for (const [target, pairing] of pairings) {
		if (!target || !target.isConnected) {
			clearTypewriterBackgroundReveal(target)
			pairings.delete(target)
			continue
		}
		// Compute the rect once per pairing per tick and pass it to
		// updatePairing — was previously walked twice (once in
		// isNearViewport, once in updatePairing's getProgress).
		const rect = getNaturalViewportRect(target)
		const ctx = getViewportContext(getScrollContainerForElement(target))
		if (!isRectNearViewport(rect, ctx.vh)) continue
		anyNear = true
                      
		const _perfElStart = performance.now()
            
		if (shouldHoldPairingAtZeroDuringIntro(target)) {
			applyPairingProgress(target, pairing, 0)
		} else {
			updatePairing(target, pairing, rect, ctx)
		}
                      
		recordPairingPerf(target, pairing, performance.now() - _perfElStart)
            
	}

                     
	flushScrubPerfIfDue(performance.now() - _perfTickStart, anyNear)
           

	if (anyNear && scrollIdleTimeoutId !== null) {
		rafId = requestAnimationFrame(tick)
	} else {
		rafId = null
	}
}

                    
const _scrubPerfBucket = {
	tickCount: 0,
	totalTickMs: 0,
	maxTickMs: 0,
	pairings: new Map(),
	flushAtMs: 0,
}
function describePairingForPerf(target, pairing) {
	const tag = (target.tagName || '?').toLowerCase()
	const classes = (target.className || '').toString().split(/\s+/).filter(Boolean).slice(0, 2)
	const classPart = classes.length ? '.' + classes.join('.') : ''
	const charCount = pairing.characters ? pairing.characters.length : null
	const extra = pairing.type + (charCount != null ? ',n=' + charCount : '')
	return tag + classPart + '[' + extra + ']'
}
function recordPairingPerf(target, pairing, ms) {
	let entry = _scrubPerfBucket.pairings.get(target)
	if (!entry) {
		entry = { totalMs: 0, maxMs: 0, count: 0, label: describePairingForPerf(target, pairing) }
		_scrubPerfBucket.pairings.set(target, entry)
	}
	entry.totalMs += ms
	if (ms > entry.maxMs) entry.maxMs = ms
	entry.count++
}
function flushScrubPerfIfDue(tickMs, anyNear) {
	if (anyNear) {
		_scrubPerfBucket.tickCount++
		_scrubPerfBucket.totalTickMs += tickMs
		if (tickMs > _scrubPerfBucket.maxTickMs) _scrubPerfBucket.maxTickMs = tickMs
	}
	const now = performance.now()
	if (_scrubPerfBucket.flushAtMs === 0) _scrubPerfBucket.flushAtMs = now + 1000
	if (now < _scrubPerfBucket.flushAtMs || _scrubPerfBucket.tickCount === 0) return

	const avg = _scrubPerfBucket.totalTickMs / _scrubPerfBucket.tickCount
	const ranked = []
	_scrubPerfBucket.pairings.forEach((entry) => {
		ranked.push({
			label: entry.label,
			avgMs: entry.totalMs / entry.count,
			maxMs: entry.maxMs,
			count: entry.count,
		})
	})
	ranked.sort((a, b) => b.avgMs - a.avgMs)
	console.log(
		'[text-scrub perf] ticks=' +
			_scrubPerfBucket.tickCount +
			' avgTick=' +
			avg.toFixed(2) +
			'ms maxTick=' +
			_scrubPerfBucket.maxTickMs.toFixed(2) +
			'ms — top:',
	)
	for (let i = 0; i < Math.min(5, ranked.length); i++) {
		const r = ranked[i]
		console.log(
			'  ' +
				(i + 1) +
				'. avg=' +
				r.avgMs.toFixed(2) +
				'ms max=' +
				r.maxMs.toFixed(2) +
				'ms (' +
				r.count +
				'×) — ' +
				r.label,
		)
	}
	_scrubPerfBucket.tickCount = 0
	_scrubPerfBucket.totalTickMs = 0
	_scrubPerfBucket.maxTickMs = 0
	_scrubPerfBucket.pairings = new Map()
	_scrubPerfBucket.flushAtMs = now + 1000
}
          

const ensureLoopRunning = () => {
	if (rafId == null) {
		rafId = requestAnimationFrame(tick)
	}
}

const onScrollOrResize = () => {
	cancelIntroAnimation()
	if (scrollIdleTimeoutId !== null) {
		clearTimeout(scrollIdleTimeoutId)
	}
	scrollIdleTimeoutId = window.setTimeout(() => {
		scrollIdleTimeoutId = null
	}, SCROLL_IDLE_TIMEOUT_MS)
	ensureLoopRunning()
}

const attachListeners = () => {
	if (listenersAttached) return
	listenersAttached = true
	// Capture-phase so scrolls inside sub-scrollers (`#lay-project-overlay`,
	// `.lay-overlay > .lay-content`) drive the loop too — same fix as in
	// `scroll_animations.js`.
	window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true })
	// `window_width_changed` instead of `resize`: filters out iOS browser-
	// chrome show/hide noise during scroll; real layout-affecting resizes
	// almost always change innerWidth too.
	jQuery(window).on('window_width_changed', onScrollOrResize)
}

function buildPairing(target) {
	const animType = target.getAttribute('data-animation-type') || ''
	const startAtRaw = parseFloat(target.getAttribute(START_AT_ATTR))
	const startAt = isFinite(startAtRaw) && startAtRaw > 0 ? startAtRaw : 0
	const endAtTopRaw = parseFloat(target.getAttribute(END_AT_TOP_ATTR))
	const endAtTop = isFinite(endAtTopRaw) ? endAtTopRaw : 90
	const endAtFullExit = target.hasAttribute(END_AT_FULL_EXIT_ATTR)

	if (animType === 'typewriter' || animType === 'typewriter_words' || animType === 'typewriter_lines') {
		// Use the same target list as the non-scrub timeline factory:
		// `.lay-text-line .char` PLUS each `<a class="laybutton">` collapsed to
		// the anchor itself (one stagger step per button). Without this the
		// scrub path would only animate `.char` opacity and the button — which
		// has no `.char` of its own — would stay at the pre-reveal `opacity: 0`
		// forever.
		const characters = animType === 'typewriter_lines'
			? Array.from(target.querySelectorAll('.lay-text-line'))
			: animType === 'typewriter_words'
				? getWordLevelAnimationTargets(target)
				: getCharacterLevelAnimationTargets(target)
		// Parent must be visible so the chars are seen as they reveal. Pre-
		// reveal state already set chars to opacity 0; we just make sure here
		// in case applyPreReveal didn't run for this target yet.
		target.style.opacity = '1'
		characters.forEach((c) => {
			c.style.opacity = '0'
		})
		// Mark "initialized" the same way `playAnimation` does so other modules
		// that key off this class (or downstream introspection) see consistent
		// state.
		target.classList.add('lay-text-animation-initialized')
		const reverse = shouldReverseTypewriterScrub(target, animType)
		prepareTypewriterBackgroundReveal(target, characters, reverse)
		return { type: 'typewriter', characters, reverse, startAt, endAtTop, endAtFullExit }
	}

	if (animType === 'typewriter_fade') {
		// Skip the timeline path here: anime.js's per-char tween updates fire
		// for every char on every `seek`, with no shortcut for chars already
		// stable at 0 or 1. For long text that's a frame-budget hog. We do
		// the opacity math directly and cache the last value per char so
		// stable chars don't trigger style writes.
		// Same laybutton-collapsing rule as the typewriter branch above —
		// otherwise the `<a>` wrapper stays at pre-reveal opacity:0.
		const characters = getCharacterLevelAnimationTargets(target)
		target.style.opacity = '1'
		characters.forEach((c) => {
			c.style.opacity = '0'
		})
		target.classList.add('lay-text-animation-initialized')
		// Mirror what `createAnimationTimeline` would have configured: stagger
		// from data-duration, initial offset from data-delay, anime.js default
		// 1000ms tween per char (matches the timeline-built version).
		const baseDelay = parseInt(target.getAttribute('data-duration'), 10) || 40
		const initialDelay = parseInt(target.getAttribute('data-delay'), 10) || 0
		const tweenDuration = 1000
		const totalDuration = Math.max(
			1,
			initialDelay + (characters.length > 0 ? (characters.length - 1) * baseDelay : 0) + tweenDuration,
		)
		const easingName = target.getAttribute('data-animation-easing') || 'easeOutCubic'
		const easingFn =
			typeof anime !== 'undefined' && typeof anime.easing === 'function'
				? anime.easing(easingName)
				: function (n) {
						return n
				  }
		return {
			type: 'typewriter_fade',
			characters,
			startAt,
			endAtTop,
			endAtFullExit,
			baseDelay,
			initialDelay,
			tweenDuration,
			totalDuration,
			easingFn,
		}
	}

	// Timeline-driven types (mask_*, fade_*, typewriter_fade). Reuse the
	// existing factory so the per-tween logic stays in ONE place.
	if (animType !== 'fade_whole_text' && animType !== 'fade_and_slide_whole_text') {
		target.style.opacity = '1'
	}
	// Always build a fresh timeline. Reusing a cached one is unsafe: the DOM
	// can be re-split between events (Splitting.js fallback fires first, then
	// pretext finishes lazy-load and re-splits), and a timeline built before
	// the second split has zero per-target animations because the .word /
	// .lay-text-line elements didn't exist yet. Drop the old one (if any) so
	// `createAnimationTimeline` rebuilds against the current DOM.
	const previous = animationTimelines.get(target)
	if (previous && typeof previous.pause === 'function') previous.pause()
	animationTimelines.delete(target)
	const timeline = createAnimationTimeline(target)
	if (timeline && typeof timeline.pause === 'function') {
		timeline.pause()
	}
	// For masked_* types: assert the clip once + neutralise the timeline's
	// own `complete` callback. The factory installs a complete that calls
	// `setLineTextMaskRevealClip(lineMasks, false)` when progress hits 1 —
	// fine for one-shot play, but in scrub mode reaching progress=1 must
	// keep the mask intact so reverse-scrolling can hide the words again.
	// Overriding to a no-op means we don't have to re-assert the clip every
	// tick (which was a measurable cost for typewriter-heavy pages).
	const isMaskedType =
		animType === 'masked_lines' || animType === 'masked_words' || animType === 'masked_characters'
	const lineMasks = isMaskedType ? target.querySelectorAll('.lay-text-line-mask') : null
	if (lineMasks && lineMasks.length > 0) {
		setLineTextMaskRevealClip(lineMasks, true)
	}
	if (isMaskedType && timeline) {
		// anime.js's `b('complete')` reads `k.complete`; replacing with a
		// no-op is enough — there are no other side-effects in the original
		// callback besides the unclip.
		timeline.complete = function () {}
	}
	target.classList.add('lay-text-animation-initialized')
	return { type: animType, timeline, startAt, endAtTop, endAtFullExit, lineMasks }
}

function resetAndDeletePairing(target) {
	cancelIntroAnimation()
	const pairing = pairings.get(target)
	if (pairing && pairing.timeline && typeof pairing.timeline.pause === 'function') {
		pairing.timeline.pause()
	}
	const timeline = animationTimelines.get(target)
	if (timeline && typeof timeline.pause === 'function') {
		timeline.pause()
	}
	animationTimelines.delete(target)
	pairings.delete(target)
	clearTypewriterBackgroundReveal(target)
	applyPreRevealVisualStateToTarget(target)
	target.classList.remove('text-animation-in-view')
	target.classList.add('text-animation-never-played')
}

function resetProjectOverlayPairings() {
	document.querySelectorAll('#lay-project-overlay [' + SCRUB_ATTR + '="true"]').forEach((target) => {
		resetAndDeletePairing(target)
	})
}

function mutationFilterFromOptions(options) {
	if (!options || typeof options !== 'object') return null
	const resize = options.resizeMutationTargets
	const first = options.firstSplitMutationTargets
	if (!resize && !first) return null
	const merged = new Set()
	if (resize && typeof resize.forEach === 'function') resize.forEach((t) => merged.add(t))
	if (first && typeof first.forEach === 'function') first.forEach((t) => merged.add(t))
	return merged
}

function refreshPairings(options = {}) {
	if (prefersReducedMotion) {
		// Reduce-motion: just snap everything to end state, don't bother with
		// scroll tracking.
		const els = Array.from(document.querySelectorAll('[' + SCRUB_ATTR + '="true"]'))
		els.forEach((el) => {
			const pairing = buildPairing(el)
			snapPairingToEnd(el, pairing)
		})
		return
	}

	// Page-overlay text elements have their own pipeline (separate timeline
	// factory + map in textanimations_pageoverlays.js), driven by
	// `scrub_for_pageoverlays.js`. Skip anything inside a `.lay-overlay`
	// so timelines built here don't leak across maps on resize.
	const all = Array.from(document.querySelectorAll('[' + SCRUB_ATTR + '="true"]'))
	const found = new Set(
		all.filter((el) => {
			if (el.closest('.lay-overlay')) return false
			if (el.closest('.lay-row-fixed')) return false
			if (options.projectOverlayOnly && !el.closest('#lay-project-overlay')) return false
			if (options.normalPageOnly && el.closest('#lay-project-overlay')) return false
			return true
		})
	)

	// Drop pairings whose target left the DOM or no longer has the attribute.
	for (const target of Array.from(pairings.keys())) {
		if (options.projectOverlayOnly && !target.closest('#lay-project-overlay')) {
			continue
		}
		if (options.normalPageOnly && target.closest('#lay-project-overlay')) {
			continue
		}
		if (!found.has(target)) {
			clearTypewriterBackgroundReveal(target)
			pairings.delete(target)
		}
	}

	// When called from `texts_are_split`, the event detail carries the
	// targets whose DOM was actually mutated. Restrict rebuilds to those —
	// otherwise the underlay's content_ready in the direct-load flow would
	// rebuild already-stable overlay pairings and snap their chars to
	// opacity 0 (visible flicker mid-intro). Other callers (no detail) get
	// the previous "rebuild all" behavior.
	const mutationFilter = mutationFilterFromOptions(options)
	const targetsToBuild = mutationFilter
		? new Set(Array.from(found).filter((t) => mutationFilter.has(t)))
		: found

	// Re-build pairings — the DOM may have been re-split (e.g., pretext
	// lazy-load finished after Splitting.js fallback already ran) and the
	// timeline children must be rebuilt against the now-correct elements.
	// Reset diag flag so we re-emit the diagnostic once the new timeline is
	// in use.
	targetsToBuild.forEach((target) => {
		const prevPairing = pairings.get(target)
		if (prevPairing && prevPairing.timeline) {
			prevPairing.timeline.__scrubDiagDone = false
		}
		if (prevPairing && prevPairing.type === 'typewriter') {
			clearTypewriterBackgroundReveal(target)
		}
		pairings.set(target, buildPairing(target))
	})

	if (pairings.size === 0) {
		// Nothing to drive — let any pending rAF settle on its own.
		return
	}
	attachListeners()

	if (options.animateInitialProgress) {
		animateInitialPairingsToCurrentProgress(found)
		return
	}

	// Initial paint: every paired element gets its progress applied so we
	// don't show a flash at progress=0 while waiting for the first scroll.
	// When a mutation filter is set, only paint the just-rebuilt targets —
	// re-painting an already-stable pairing in another scope would risk
	// snapping it during a different scope's intro animation (the direct-
	// load underlay-load case that produced the visible scrub flicker).
	pairings.forEach((pairing, target) => {
		if (mutationFilter && !mutationFilter.has(target)) return
		const rect = getNaturalViewportRect(target)
		const initCtx = getViewportContext(getScrollContainerForElement(target))
		if (shouldHoldPairingAtZeroDuringIntro(target)) {
			applyPairingProgress(target, pairing, 0)
			return
		}
		updatePairing(target, pairing, rect, initCtx)
	})

	// Kick the loop so currently-near elements are reconciled even before the
	// user scrolls. Skip when an intro RAF is in flight — `onScrollOrResize`
	// calls `cancelIntroAnimation`, which would kill an intro mid-stagger
	// (e.g. direct-load underlay's content_ready firing while the overlay's
	// scrub intro is still in its delay phase). The scrub tick will pick up
	// these pairings the next time the user scrolls.
	if (!animation_lifecycle_controller.isAnyIntroPending() && introRafId === null) {
		onScrollOrResize()
	}
}

const i = () => {
	const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
	prefersReducedMotion = mq.matches

	if (window.laytheme && typeof window.laytheme.on === 'function') {
		// `texts_are_split` fires after splitting + pre-reveal, so the DOM is
		// in the right shape to build timelines from.
		window.laytheme.on('texts_are_split', refreshPairings)
		// AJAX nav (project overlays + same-page nav) — safety net in case the
		// new content didn't go through `texts_are_split` for some reason.
		// Scope to the event's source so a future double-fire (overlay + underlay
		// in the direct-load flow) doesn't tear down the other scope's pairings
		// mid-animation.
		window.laytheme.on('newpageshown', function (layoutObj, type, obj, isProjectOverlayObj) {
			const isProjectOverlay = isProjectOverlayObj && isProjectOverlayObj.isProjectOverlay === true
			refreshPairings(isProjectOverlay ? { projectOverlayOnly: true } : { normalPageOnly: true })
		})
		// SET-TRUE for the intro-pending flags is owned by the lifecycle
		// controller (Phase 3 second pass). These listeners only do scrub-
		// internal cleanup work that's coupled with the lifecycle event
		// timing; the controller's own listener for the same event runs
		// first because the controller is imported (and thus its
		// `installLifecycleEventListeners()` runs) before this `i()` call.
		window.laytheme.on('before_content_replacement', () => {
			if (animation_lifecycle_controller.isIntroPendingFor('normal')) {
				cancelIntroAnimation()
			}
		})
		window.laytheme.on('transition_navigation_in_started', (type) => {
			// Overlay-open fires this event with type='project_overlay' while the
			// underlay page is still in the DOM and expected to finish its own
			// animation behind the overlay. Skip the cancel in that case so a
			// running underlay intro RAF isn't killed mid-flight.
			if (type === 'project_overlay') return
			if (animation_lifecycle_controller.isIntroPendingFor('normal')) {
				cancelIntroAnimation()
			}
		})
		window.laytheme.on('project_overlay_before_animate_in', () => {
			resetProjectOverlayPairings()
		})
		window.laytheme.on('transition_navigation_in_completed', () => {
			// Overlay-open intro: re-fade overlay scrub targets in lockstep
			// once the nav transition finishes. Normal-page scrubs no longer
			// need any post-nav handling now that nav-sync is gone — they
			// just follow scroll position from the start.
			if (animation_lifecycle_controller.isIntroPendingFor('overlay')) {
				animation_lifecycle_controller.setIntroPending('normal', false)
				animation_lifecycle_controller.setIntroPending('overlay', false)
				refreshPairings({ projectOverlayOnly: true, animateInitialProgress: true })
				return
			}
			// Normal-page intro: just clear the pending flag — nothing to
			// replay since scrub targets aren't held at zero anymore.
			if (animation_lifecycle_controller.isIntroPendingFor('normal')) {
				animation_lifecycle_controller.setIntroPending('normal', false)
			}
		})
		window.laytheme.on('projectoverlayclosed', () => {
			animation_lifecycle_controller.setIntroPending('normal', false)
			animation_lifecycle_controller.setIntroPending('overlay', false)
			resetProjectOverlayPairings()
		})
	}

	if (mq.addEventListener) {
		mq.addEventListener('change', (e) => {
			prefersReducedMotion = e.matches
			if (prefersReducedMotion) {
				pairings.forEach((pairing, target) => snapPairingToEnd(target, pairing))
			}
		})
	}
}

const text_animation_scrub_controller = {
	i: i,
}

export default text_animation_scrub_controller
